import {ApplicationModalStep} from "../types/ApplicationModalStep";

export interface IApplicationModalStateContext {
    isMultileadEnabled: boolean;
    needsMarketingConsent: boolean;
    needsHomeMatchBasicPreferences: boolean;
    isAutoresponderEnabled: boolean;
    forceClose: boolean;
    hasMultileadBeenShown: boolean;
    hasHomeMatchBasicPreferencesBeenShown: boolean;
}

/**
 * The part of the context derived from the offer/property data, which is kept in sync while the modal is open.
 * The lead-response flags (`needsMarketingConsent`, `needsHomeMatchBasicPreferences`) are set by the event that
 * carries the lead response and must survive those syncs - the preferences survey now runs after the multilead,
 * so its flag has to stay intact for as long as the user browses the recommendations.
 */
export type IApplicationModalStateDataContext = Pick<IApplicationModalStateContext, "isMultileadEnabled" | "isAutoresponderEnabled" | "forceClose">;

export type ApplicationModalStateEvent =
    | {type: "OPEN"; step?: ApplicationModalStep}
    | {type: "FORM_SUBMITTED"}
    | {type: "FORM_CLOSED"}
    | {type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"}
    | {type: "PROMOTION_SMS_LOGIN_REQUIRED"}
    | {type: "PROMOTION_AUTH_REQUIRED"}
    | {type: "PROMOTION_SUBMIT_STARTED"}
    | {type: "PROMOTION_SUBMIT_SUCCEEDED"}
    | {type: "PROMOTION_SUBMIT_FAILED"; step?: ApplicationModalStep}
    | {type: "PROMOTION_EDIT_PHONE"; step?: ApplicationModalStep}
    | {type: "MULTILEAD_COMPLETED"}
    | {type: "MULTILEAD_NO_RECOMMENDATIONS"}
    | {type: "MULTILEAD_CLOSED"}
    | {type: "CONSENT_COMPLETED"}
    | {type: "SUCCESS_INFO_NEXT"}
    | {type: "HOME_MATCH_BASIC_COMPLETED"};

type TransitionFn = (ctx: IApplicationModalStateContext, event: ApplicationModalStateEvent) => ApplicationModalStep;
type StepTransitions = Partial<Record<ApplicationModalStateEvent["type"], TransitionFn>>;

const shouldShowPostLeadMultilead = (ctx: IApplicationModalStateContext) => ctx.isMultileadEnabled && !ctx.hasMultileadBeenShown;
const shouldShowHomeMatchBasicPreferences = (ctx: IApplicationModalStateContext) =>
    ctx.needsHomeMatchBasicPreferences && !ctx.hasHomeMatchBasicPreferencesBeenShown;

// Post-lead order: propose first (multilead), ask afterwards (preferences survey)
const handleFormSubmitted: TransitionFn = (ctx) => {
    if (shouldShowPostLeadMultilead(ctx)) return ApplicationModalStep.MULTILEAD;
    if (shouldShowHomeMatchBasicPreferences(ctx)) return ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES;
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

const handleFormClosed: TransitionFn = () => ApplicationModalStep.CLOSE;

const handleFormClosedWithGoodbye: TransitionFn = (ctx) => {
    if (ctx.isAutoresponderEnabled || ctx.forceClose) return ApplicationModalStep.CLOSE;
    return ApplicationModalStep.GOODBYE_INFO;
};

const getEventStep = (event: ApplicationModalStateEvent) => ("step" in event ? event.step : undefined);

const handleEditPhone: TransitionFn = (_ctx, event) => getEventStep(event) ?? ApplicationModalStep.PROMOTION_FORM;
const handleSubmitFailed: TransitionFn = (_ctx, event) => getEventStep(event) ?? ApplicationModalStep.PROMOTION_FORM;

const leadFormVerificationTransitions = {
    PROMOTION_PHONE_VERIFICATION_REQUIRED: () => ApplicationModalStep.PROMOTION_PHONE_VERIFICATION,
    PROMOTION_SMS_LOGIN_REQUIRED: () => ApplicationModalStep.PROMOTION_SMS_LOGIN_VERIFICATION,
    PROMOTION_AUTH_REQUIRED: () => ApplicationModalStep.PROMOTION_AUTH_FALLBACK,
    PROMOTION_EDIT_PHONE: handleEditPhone
};

// Going through the multilead - sending the extra inquiries or having nothing to recommend - continues
// to the preferences survey, so the survey is never skipped just because the multilead now comes first.
const handleMultileadNext: TransitionFn = (ctx) => {
    if (shouldShowHomeMatchBasicPreferences(ctx)) return ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES;
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

// Dismissing the multilead still has to reach the preferences survey, but - unlike going through it -
// it never pulls in the marketing consent step, so dismissing keeps ending the flow as it did before the reorder.
const handleMultileadDismissed: TransitionFn = (ctx) => {
    if (shouldShowHomeMatchBasicPreferences(ctx)) return ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES;
    return ApplicationModalStep.CLOSE;
};

// The preferences survey is the last content step of the post-lead flow
const handleHomeMatchBasicCompleted: TransitionFn = (ctx) => {
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

const transitions: Partial<Record<ApplicationModalStep, StepTransitions>> = {
    [ApplicationModalStep.WAITING]: {
        OPEN: (_ctx, event) => getEventStep(event) ?? ApplicationModalStep.APPLICATION_FORM_STANDARD
    },

    [ApplicationModalStep.APPLICATION_FORM_STANDARD]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosedWithGoodbye
    },

    [ApplicationModalStep.APPLICATION_FORM_FULL_OFFER]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_FORM]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_PHONE_VERIFICATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        PROMOTION_EDIT_PHONE: handleEditPhone,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_SMS_LOGIN_VERIFICATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        PROMOTION_AUTH_REQUIRED: () => ApplicationModalStep.PROMOTION_AUTH_FALLBACK,
        PROMOTION_EDIT_PHONE: handleEditPhone,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        PROMOTION_SUBMIT_FAILED: handleSubmitFailed,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_AUTH_FALLBACK]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        PROMOTION_SUBMIT_FAILED: handleSubmitFailed,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_SUBMITTING]: {
        PROMOTION_SUBMIT_SUCCEEDED: () => ApplicationModalStep.PROMOTION_SUCCESS,
        PROMOTION_SUBMIT_FAILED: handleSubmitFailed,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_SUCCESS]: {
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.MULTILEAD]: {
        MULTILEAD_COMPLETED: handleMultileadNext,
        MULTILEAD_NO_RECOMMENDATIONS: handleMultileadNext,
        MULTILEAD_CLOSED: handleMultileadDismissed,
        FORM_CLOSED: handleMultileadDismissed
    },

    [ApplicationModalStep.NOTIFICATION_CONSENT]: {
        CONSENT_COMPLETED: () => ApplicationModalStep.CLOSE,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.GOODBYE_INFO]: {
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.SUCCESS_INFO]: {
        SUCCESS_INFO_NEXT: () => ApplicationModalStep.CLOSE,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES]: {
        HOME_MATCH_BASIC_COMPLETED: handleHomeMatchBasicCompleted,
        FORM_CLOSED: handleHomeMatchBasicCompleted
    }
};

/**
 * Resolves the next step given the current step, an event, and the application modal state context.
 * Returns null if no valid transition exists for the current step + event combination.
 */
export function resolveNextStep(
    currentStep: ApplicationModalStep,
    event: ApplicationModalStateEvent,
    context: IApplicationModalStateContext
): ApplicationModalStep | null {
    const stepTransitions = transitions[currentStep];
    if (!stepTransitions) return null;

    const transitionFn = stepTransitions[event.type];
    if (!transitionFn) return null;

    return transitionFn(context, event);
}
import {ApplicationModalStep} from "../types/ApplicationModalStep";

export interface IApplicationModalStateContext {
    isMultileadEnabled: boolean;
    needsMarketingConsent: boolean;
    needsHomeMatchBasicPreferences: boolean;
    isAutoresponderEnabled: boolean;
    forceClose: boolean;
    hasMultileadBeenShown: boolean;
    hasHomeMatchBasicPreferencesBeenShown: boolean;
}

/**
 * The part of the context derived from the offer/property data, which is kept in sync while the modal is open.
 * The lead-response flags (`needsMarketingConsent`, `needsHomeMatchBasicPreferences`) are set by the event that
 * carries the lead response and must survive those syncs - the preferences survey now runs after the multilead,
 * so its flag has to stay intact for as long as the user browses the recommendations.
 */
export type IApplicationModalStateDataContext = Pick<IApplicationModalStateContext, "isMultileadEnabled" | "isAutoresponderEnabled" | "forceClose">;

export type ApplicationModalStateEvent =
    | {type: "OPEN"; step?: ApplicationModalStep}
    | {type: "FORM_SUBMITTED"}
    | {type: "FORM_CLOSED"}
    | {type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"}
    | {type: "PROMOTION_SMS_LOGIN_REQUIRED"}
    | {type: "PROMOTION_AUTH_REQUIRED"}
    | {type: "PROMOTION_SUBMIT_STARTED"}
    | {type: "PROMOTION_SUBMIT_SUCCEEDED"}
    | {type: "PROMOTION_SUBMIT_FAILED"; step?: ApplicationModalStep}
    | {type: "PROMOTION_EDIT_PHONE"; step?: ApplicationModalStep}
    | {type: "MULTILEAD_COMPLETED"}
    | {type: "MULTILEAD_NO_RECOMMENDATIONS"}
    | {type: "MULTILEAD_CLOSED"}
    | {type: "CONSENT_COMPLETED"}
    | {type: "SUCCESS_INFO_NEXT"}
    | {type: "HOME_MATCH_BASIC_COMPLETED"};

type TransitionFn = (ctx: IApplicationModalStateContext, event: ApplicationModalStateEvent) => ApplicationModalStep;
type StepTransitions = Partial<Record<ApplicationModalStateEvent["type"], TransitionFn>>;

const shouldShowPostLeadMultilead = (ctx: IApplicationModalStateContext) => ctx.isMultileadEnabled && !ctx.hasMultileadBeenShown;
const shouldShowHomeMatchBasicPreferences = (ctx: IApplicationModalStateContext) =>
    ctx.needsHomeMatchBasicPreferences && !ctx.hasHomeMatchBasicPreferencesBeenShown;

// Post-lead order: propose first (multilead), ask afterwards (preferences survey)
const handleFormSubmitted: TransitionFn = (ctx) => {
    if (shouldShowPostLeadMultilead(ctx)) return ApplicationModalStep.MULTILEAD;
    if (shouldShowHomeMatchBasicPreferences(ctx)) return ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES;
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

const handleFormClosed: TransitionFn = () => ApplicationModalStep.CLOSE;

const handleFormClosedWithGoodbye: TransitionFn = (ctx) => {
    if (ctx.isAutoresponderEnabled || ctx.forceClose) return ApplicationModalStep.CLOSE;
    return ApplicationModalStep.GOODBYE_INFO;
};

const getEventStep = (event: ApplicationModalStateEvent) => ("step" in event ? event.step : undefined);

const handleEditPhone: TransitionFn = (_ctx, event) => getEventStep(event) ?? ApplicationModalStep.PROMOTION_FORM;
const handleSubmitFailed: TransitionFn = (_ctx, event) => getEventStep(event) ?? ApplicationModalStep.PROMOTION_FORM;

const leadFormVerificationTransitions = {
    PROMOTION_PHONE_VERIFICATION_REQUIRED: () => ApplicationModalStep.PROMOTION_PHONE_VERIFICATION,
    PROMOTION_SMS_LOGIN_REQUIRED: () => ApplicationModalStep.PROMOTION_SMS_LOGIN_VERIFICATION,
    PROMOTION_AUTH_REQUIRED: () => ApplicationModalStep.PROMOTION_AUTH_FALLBACK,
    PROMOTION_EDIT_PHONE: handleEditPhone
};

// Going through the multilead - sending the extra inquiries or having nothing to recommend - continues
// to the preferences survey, so the survey is never skipped just because the multilead now comes first.
const handleMultileadNext: TransitionFn = (ctx) => {
    if (shouldShowHomeMatchBasicPreferences(ctx)) return ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES;
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

// Dismissing the multilead still has to reach the preferences survey, but - unlike going through it -
// it never pulls in the marketing consent step, so dismissing keeps ending the flow as it did before the reorder.
const handleMultileadDismissed: TransitionFn = (ctx) => {
    if (shouldShowHomeMatchBasicPreferences(ctx)) return ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES;
    return ApplicationModalStep.CLOSE;
};

// The preferences survey is the last content step of the post-lead flow
const handleHomeMatchBasicCompleted: TransitionFn = (ctx) => {
    if (ctx.needsMarketingConsent) return ApplicationModalStep.NOTIFICATION_CONSENT;
    return ApplicationModalStep.CLOSE;
};

const transitions: Partial<Record<ApplicationModalStep, StepTransitions>> = {
    [ApplicationModalStep.WAITING]: {
        OPEN: (_ctx, event) => getEventStep(event) ?? ApplicationModalStep.APPLICATION_FORM_STANDARD
    },

    [ApplicationModalStep.APPLICATION_FORM_STANDARD]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosedWithGoodbye
    },

    [ApplicationModalStep.APPLICATION_FORM_FULL_OFFER]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_FORM]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_PHONE_VERIFICATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        PROMOTION_EDIT_PHONE: handleEditPhone,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_SMS_LOGIN_VERIFICATION]: {
        FORM_SUBMITTED: handleFormSubmitted,
        PROMOTION_AUTH_REQUIRED: () => ApplicationModalStep.PROMOTION_AUTH_FALLBACK,
        PROMOTION_EDIT_PHONE: handleEditPhone,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        PROMOTION_SUBMIT_FAILED: handleSubmitFailed,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_AUTH_FALLBACK]: {
        FORM_SUBMITTED: handleFormSubmitted,
        ...leadFormVerificationTransitions,
        PROMOTION_SUBMIT_STARTED: () => ApplicationModalStep.PROMOTION_SUBMITTING,
        PROMOTION_SUBMIT_FAILED: handleSubmitFailed,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_SUBMITTING]: {
        PROMOTION_SUBMIT_SUCCEEDED: () => ApplicationModalStep.PROMOTION_SUCCESS,
        PROMOTION_SUBMIT_FAILED: handleSubmitFailed,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.PROMOTION_SUCCESS]: {
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.MULTILEAD]: {
        MULTILEAD_COMPLETED: handleMultileadNext,
        MULTILEAD_NO_RECOMMENDATIONS: handleMultileadNext,
        MULTILEAD_CLOSED: handleMultileadDismissed,
        FORM_CLOSED: handleMultileadDismissed
    },

    [ApplicationModalStep.NOTIFICATION_CONSENT]: {
        CONSENT_COMPLETED: () => ApplicationModalStep.CLOSE,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.GOODBYE_INFO]: {
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.SUCCESS_INFO]: {
        SUCCESS_INFO_NEXT: () => ApplicationModalStep.CLOSE,
        FORM_CLOSED: handleFormClosed
    },

    [ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES]: {
        HOME_MATCH_BASIC_COMPLETED: handleHomeMatchBasicCompleted,
        FORM_CLOSED: handleHomeMatchBasicCompleted
    }
};

/**
 * Resolves the next step given the current step, an event, and the application modal state context.
 * Returns null if no valid transition exists for the current step + event combination.
 */
export function resolveNextStep(
    currentStep: ApplicationModalStep,
    event: ApplicationModalStateEvent,
    context: IApplicationModalStateContext
): ApplicationModalStep | null {
    const stepTransitions = transitions[currentStep];
    if (!stepTransitions) return null;

    const transitionFn = stepTransitions[event.type];
    if (!transitionFn) return null;

    return transitionFn(context, event);
}
